import type { Invoice } from "src/types";
import { InvoiceStatus } from "src/constants/invoice";

export const isStatusSendable = (inv: Invoice) =>
    inv.status !== InvoiceStatus.PAID &&
    inv.status !== InvoiceStatus.WRITTEN_OFF &&
    inv.status !== InvoiceStatus.DELETED;

export const hasEmail = (inv: Invoice) => !!inv.recipient?.email;

export const hasAddress = (inv: Invoice) =>
    !!(inv.recipient?.street && inv.recipient?.plz && inv.recipient?.city);

/** Missing Einwilligung for post — the backend's CONSENT_REQUIRED gate in
 *  sendViaLetterXpressController. postConsentAccepted is computed by invoiceController with the
 *  same rule (questionnaire || digital || post consent), so this never disagrees with the server. */
export const isPostConsentBlocked = (inv: Invoice, requireLegalAcceptance: boolean) =>
    requireLegalAcceptance && !(inv.postConsentAccepted ?? false);

/** "Ready to go out by post": not sent yet (no portal link, no letter), still open, has a full
 *  address and postal consent — and digital sending isn't an option for it: post-only patient,
 *  no email, or only postal (not digital) consent. */
export const isReadyForPost = (inv: Invoice, requireLegalAcceptance: boolean) => {
    if (inv.sentDate || inv.letterXpressId) return false;
    if (!isStatusSendable(inv) || !hasAddress(inv)) return false;
    if (isPostConsentBlocked(inv, requireLegalAcceptance)) return false;
    const postOnly = inv.patient?.postOnly ?? false;
    const digitalBlocked = requireLegalAcceptance && !(inv.questionnaireAccepted ?? false);
    return postOnly || !hasEmail(inv) || digitalBlocked;
};

export interface BulkLetterSplit {
    canSend: Invoice[];
    noConsent: Invoice[];
    letterSent: Invoice[];
    noAddress: Invoice[];
    cannotSend: Invoice[];
}

/** Sorts a bulk-post selection into disjoint groups, in the order the backend refuses them:
 *  paid / written off, no consent (CONSENT_REQUIRED), letter already sent (409), no address.
 *  Only canSend is priced and sent. */
export function splitForBulkLetter(invoices: Invoice[], requireLegalAcceptance: boolean): BulkLetterSplit {
    const split: BulkLetterSplit = { canSend: [], noConsent: [], letterSent: [], noAddress: [], cannotSend: [] };
    for (const inv of invoices) {
        if (!isStatusSendable(inv)) split.cannotSend.push(inv);
        else if (isPostConsentBlocked(inv, requireLegalAcceptance)) split.noConsent.push(inv);
        else if (inv.letterXpressId) split.letterSent.push(inv);
        else if (!hasAddress(inv)) split.noAddress.push(inv);
        else split.canSend.push(inv);
    }
    return split;
}
